import { Button, List, Segmented, Space, Switch } from 'antd';
import AddDroneDialog from "../../dialogs/add-drone-dialog/add-drone-dialog";
import { useEffect, useState } from "react";
import { IDrone } from "@drone-parts.garphild.pro/interfaces";
import DroneCard from "./components/drone-card";
import useDronesList from "../../helpers/hooks/useDronesList";
import Loader from "../../components/loader";
import ToOrderList from "./components/toOrderList";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { selectedDroneIdAtom } from "../../atoms/droneAtoms";
import {
  availableDroneModelsAtom,
  currentPageAtom,
  inventarizationModeAtom,
  pageSizeAtom,
  selectedProductTypeAtom,
  showSentAtom,
} from "./state";
import DronesPageToolbar from "./components/toolbar";
import { PRODUCT_TYPES, PRODUCT_TYPE_MAP } from "./product-types";

export function DronesPage() {
  const setSelectedDroneId = useSetAtom(selectedDroneIdAtom);
  const [currentDrone, setCurrentDrone] = useState<IDrone | undefined>(undefined);
  const [inventarizationMode, setInventarizationMode] = useAtom(inventarizationModeAtom);
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);
  const [showSent, setShowSent] = useAtom(showSentAtom);
  const availableDroneModels = useAtomValue(availableDroneModelsAtom);
  const [selectedProductType, setSelectedProductType] = useAtom(selectedProductTypeAtom);
  const productTypeConfig = PRODUCT_TYPE_MAP[selectedProductType];

  const {
    dronesList,
    dronesTotal,
    status,
    refresh,
    droneMutation,
    toOrder,
  } = useDronesList();

  const onResize  = () => {
    const calculatedItemsInRow = Math.min(5, Math.floor((window.innerWidth - 200) / 450)) || 1;
    setPageSize(() => calculatedItemsInRow * 3);
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    }
  }, []);


  const handleAddDrone = () => {
    if (!currentDrone) {
      const newdrone: IDrone = {
        _id: '',
        droneId: '',
        itemType: selectedProductType,
        total: 0,
        parts: [],
        status: 'draft',
        income: 0,
      }
      setCurrentDrone(() => newdrone);
      setSelectedDroneId(newdrone._id);
    } else {
      setCurrentDrone(() => undefined);
    }
  }

  const toOrderData = Object
    .keys(toOrder || {})
    .map((itemId) => toOrder?.[itemId]);
  toOrderData
    .push({
      shortName: 'Total',
      quantity: Object.keys(toOrder || {}).reduce((acc, itemId) => {
        if (toOrder?.[itemId]?.shortName === '6S2P') return acc;
        return acc + toOrder?.[itemId]?.quantity;
      }, 0),
      total: Object.keys(toOrder || {}).reduce((acc, itemId) => {
        if (toOrder?.[itemId]?.shortName === '6S2P') return acc;
        return acc + toOrder?.[itemId]?.total;
      }, 0),
    });

  return (
    <>
      {status === 'loading' && <Loader />}
      <Segmented
        value={selectedProductType}
        options={PRODUCT_TYPES.map((pt) => ({ label: pt.labelPlural, value: pt.itemType }))}
        onChange={(value) => {
          setSelectedProductType(value as string);
          setCurrentPage(0);
        }}
        style={{ marginBottom: 16 }}
      />
      <Space style={{width: '100%'}}>
        <h1>{productTypeConfig?.labelPlural || 'Products'} ({dronesTotal}) / {pageSize}</h1>
        <Button type="primary" onClick={() => refresh()}>Refresh</Button>
        <Button type="primary" onClick={() => handleAddDrone()}>{productTypeConfig?.addButtonLabel || 'Add product'}</Button>
        <Switch
          checkedChildren="Inventarization mode"
          unCheckedChildren="Show all"
          checked={inventarizationMode}
          onChange={(checked) => {
            setInventarizationMode(checked);
          }}
        />
        <Switch
          checkedChildren="Don't show sent"
          unCheckedChildren="Show sent"
          checked={showSent}
          onChange={(checked) => {
            setShowSent(checked);
          }}
        />
      </Space>
      <DronesPageToolbar
      />
      <ToOrderList
        toOrderData={toOrderData}
      />
      <Space>
        {dronesList?.reduce((acc, drone) => {
          return acc + (drone.income || drone.total) - drone.total;
        }, 0)}
      </Space>
      <List
        grid={{ gutter: 16, column: pageSize / 3 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          position: 'bottom',
          total: dronesTotal,
          onChange: (page) => {
            console.log("WREST setPage", page);
            setCurrentPage(page);
          }
        }}
        dataSource={dronesList}
        renderItem={(drone) => (
          <List.Item>
            <DroneCard
              key={drone._id}
              drone={drone}
              actions={{
                edit: () => {
                  setCurrentDrone(drone);
                  setSelectedDroneId(drone._id);
                },
                delete: () => {
                  console.log("Delete");
                },
                inventarize: inventarizationMode ? () => {
                  droneMutation
                    .mutate({ _id: drone._id, inventarized: !drone.inventarized});
                } : undefined,
              }}
              onChangeStatus={(status: string) => {
                droneMutation
                  .mutate({ _id: drone._id, status });
              }}
              onChangeIncome={(income: number) => {
                droneMutation
                  .mutate({ _id: drone._id, income });
              }}
              onPartUpdated={() => {
                refresh();
              }}
            />
          </List.Item>
        )}
      />
      {currentDrone && (
        <AddDroneDialog
          opened={true}
          onToggle={handleAddDrone}
          drone={currentDrone}
          dronesModels={availableDroneModels || []}
          onChange={(drone) => {
            console.trace("Drone changed", drone);
            setCurrentDrone(() => drone);
            setSelectedDroneId(drone._id);
          }}
          onSave={(drone) => {
            droneMutation.mutate(drone);
            setCurrentDrone(() => undefined);
          }}
        />
      )}
    {/*</Space>*/}
    </>
  )
}

export default DronesPage;
